/**
 * iDecide - Menu Button metadata types
 * Menu buttons are rendered as HTML overlays at runtime, not as IMG.LY blocks
 *
 * ARCHITECTURE:
 *
 * In Builder (Editor):
 * - Per-slide setting: `showMenuButton` boolean in SlideInfo determines if menu button appears on that slide
 * - Toggle button in navigation bar (Builder.tsx line ~1920) updates this per-slide setting
 * - No IMG.LY blocks are created - this is metadata-only
 *
 * At Runtime (Player):
 * - TODO: Read `showMenuButton` from each slide's metadata
 * - TODO: If true, render an HTML `<button>` element with `position: absolute; z-index: 9999`
 * - TODO: Button should use HAMBURGER_MENU_SVG icon and MenuButtonMeta styling
 * - TODO: On click, navigate to the target menu slide (slides with `isMenuSlide: true`)
 *
 * Global Configuration:
 * - TODO: MenuButtonMeta configuration (target slide, position, style) should be stored at presentation/session level
 *
 * This file contains only the metadata types and helper functions
 */

import {MENU_BUTTON_META_KEY, PRESENTATION_WIDTH} from "../constants.ts";
import type {RefObject} from "react";
import type {VirtualizedSlideInfo} from "../state/virtualizedSlide.ts";

/**
 * Menu Button configuration
 * Stored at presentation/session level, not per-block
 */
export type MenuButtonMeta = {
    id: string;
    elementType: string;
    targetMenuSlideId?: number;  // Which slide to navigate to when clicked
    position?: {
        x: number;  // Position as percentage (default: top-right)
        y: number;
    };
    style?: {
        iconColor?: string;
        iconSize?: number;
    };
};

export function defaultStyle(): Required<NonNullable<MenuButtonMeta['style']>> {
    return {
        iconColor: '#3A5665',
        iconSize: 40,
    };
}

export function withDefaults(meta: Partial<MenuButtonMeta>): MenuButtonMeta {
    const m: MenuButtonMeta = {
        id: meta.id || `menu-button-${Math.random().toString(36).slice(2, 8)}`,
        elementType: MENU_BUTTON_META_KEY,
        targetMenuSlideId: meta.targetMenuSlideId,
        position: meta.position || { x: 95, y: 5 },  // Top-right corner (percentages)
        style: {...defaultStyle(), ...(meta.style ?? {})},
    }

    return m;
}

// Hamburger menu SVG icon (three horizontal lines)
export const HAMBURGER_MENU_SVG = encodeURIComponent(`
<svg viewBox="0 0 100 80" xmlns="http://www.w3.org/2000/svg">
  <rect width="100" height="12" rx="6" fill="#3A5665"/>
  <rect y="34" width="100" height="12" rx="6" fill="#3A5665"/>
  <rect y="68" width="100" height="12" rx="6" fill="#3A5665"/>
</svg>
`);

const MENU_ICON_HAMBURGER_SVG = `
    <svg width="24" height="24" viewBox="0 0 24 24" aria-hidden="true" xmlns="http://www.w3.org/2000/svg">
        <line x1="4" y1="6" x2="20" y2="6" stroke="currentColor" stroke-width="2.5" stroke-linecap="round"/>
        <line x1="4" y1="12" x2="20" y2="12" stroke="currentColor" stroke-width="2.5" stroke-linecap="round"/>
        <line x1="4" y1="18" x2="20" y2="18" stroke="currentColor" stroke-width="2.5" stroke-linecap="round"/>
    </svg>
`;

const MENU_ICON_CLOSE_SVG = `
    <svg width="24" height="24" viewBox="0 0 24 24" aria-hidden="true" xmlns="http://www.w3.org/2000/svg">
        <line x1="5" y1="5" x2="19" y2="19" stroke="currentColor" stroke-width="2.5" stroke-linecap="round"/>
        <line x1="19" y1="5" x2="5" y2="19" stroke="currentColor" stroke-width="2.5" stroke-linecap="round"/>
    </svg>
`;

const setMenuButtonIcon = (iconEl: HTMLElement, isMenuOpen: boolean) => {
    iconEl.innerHTML = isMenuOpen ? MENU_ICON_CLOSE_SVG : MENU_ICON_HAMBURGER_SVG;
};

/**
 * Install menu button overlays and return a controller to manage updates/cleanup.
 * Mirrors the structure of the other overlay plugins.
 */
export type MenuButtonPluginOptions = {
    mountInteractiveOverlay?: boolean;
    onNavigateToMenu?: (menuSlideId: number) => void;
    clearHTMLOverlays?: boolean;
    getCurrentSlideId?: () => number | null | undefined;
    getCurrentPageId?: () => number | null | undefined;
    getPageIdForSlide?: (slideId: number | null | undefined) => number | null | undefined;
    getSlideIdForPage?: (pageId: number | null | undefined) => number | null | undefined;
};

export function installMenuButtonPlugin(
    cesdk: any,
    allSlideInfoRef: any,
    options: MenuButtonPluginOptions = {}
): { updateForActivePage: (pageId: number) => void; cleanup: () => void } {
    if (!cesdk?.engine?.block || options.mountInteractiveOverlay === false) {
        return {
            updateForActivePage: () => {},
            cleanup: () => {}
        };
    }

    const engine = cesdk.engine;

    let overlayContainer = document.getElementById('overlay-container');
    if (!overlayContainer) {
        const host = document.getElementById("root-shadow");
        const root = host?.shadowRoot || (host as any)?.__shadowRoot;
        overlayContainer = root?.getElementById('overlay-container');
    }

    return mountMenuButtonOverlays(engine, allSlideInfoRef, {
        container: overlayContainer,
        onNavigateToMenu: options.onNavigateToMenu,
        clearHTMLOverlays: options.clearHTMLOverlays ?? true,
        getCurrentSlideId: options.getCurrentSlideId,
        getCurrentPageId: options.getCurrentPageId,
        getPageIdForSlide: options.getPageIdForSlide,
        getSlideIdForPage: options.getSlideIdForPage,
    });
}

/**
 * Mount menu button overlays for slides that have showMenuButton enabled
 * This creates visual indicators in the builder to show where menu buttons will appear
 *
 * Returns an object with:
 * - updateForActivePage: function to call when active page changes
 * - cleanup: function to call on unmount
 */
export function mountMenuButtonOverlays(
    engine: any,
    allSlideInfoRef: any,
    opts: {
        container?: HTMLElement | null;
        onNavigateToMenu?: (menuSlideId: number) => void;
        clearHTMLOverlays?: boolean;
        getCurrentSlideId?: () => number | null | undefined;
        getCurrentPageId?: () => number | null | undefined;
        getPageIdForSlide?: (slideId: number | null | undefined) => number | null | undefined;
        getSlideIdForPage?: (pageId: number | null | undefined) => number | null | undefined;
    } = {}
): { updateForActivePage: (pageId: number) => void; cleanup: () => void } {
    const getOverlayContainer = () => {
        let el = document.getElementById('overlay-container');
        if (el) return el;
        const host = document.getElementById("root-shadow");
        const root = host?.shadowRoot || (host as any)?.__shadowRoot;
        return root?.getElementById('overlay-container');
    };
    const host = opts.container ?? getOverlayContainer() ?? document.body;

    // Optionally clear any existing menu button overlays in the container before starting
    if (opts.clearHTMLOverlays) {
        const existingOverlays: HTMLElement[] = host.querySelectorAll('.menu-button-overlay-indicator');
        existingOverlays.forEach(el => el.remove());
    }

    // Create a registry for overlays
    const overlays = new Map<number, HTMLDivElement>();

    // Find the menu slide (slide with isMenuSlide: true)
    // Always access current ref value to avoid stale closure
    const resolveSlideInfo = () => allSlideInfoRef?.current ?? allSlideInfoRef;

    const resolveSlideId = (slide: any): number | null => {
        const raw = slide?.id ?? slide?.slideId;
        return typeof raw === 'number' ? raw : null;
    };

    const getMenuSlideId = (): number | null => {
        const menuSlide = resolveSlideInfo()?.slides?.find((s: any) => s.isMenuSlide === true);
        return resolveSlideId(menuSlide);
    };

    const getSlidesWithMenuButton = () => {
        const slides = resolveSlideInfo()?.slides ?? [];
        return slides.filter((s: any) => s && (s.showMenuButton === true || s.isMenuSlide === true));
    };
    const getGlobalMenuButtonColor = (): string => {
        const slides = resolveSlideInfo()?.slides ?? [];
        const slideWithMenuButton = slides.find((s: any) => s && (s.showMenuButton === true || s.isMenuSlide === true));
        return slideWithMenuButton?.menuButtonColor || '#000000';
    };

    const resolveCurrentSlideId = () => opts.getCurrentSlideId?.() ?? null;
    const resolveCurrentPageId = () => opts.getCurrentPageId?.() ?? (engine.scene?.getCurrentPage?.() ?? null);

    const resolvePageIdForSlide = (slide: any): number | null => {
        const slideId = resolveSlideId(slide);
        let pageId = opts.getPageIdForSlide?.(slideId);
        if (pageId == null && typeof slide?.scenePageId === 'number') {
            pageId = slide.scenePageId;
        }
        if (pageId == null && slideId != null && opts.getCurrentSlideId && opts.getCurrentPageId) {
            if (slideId === opts.getCurrentSlideId()) {
                pageId = opts.getCurrentPageId() ?? null;
            }
        }
        return typeof pageId === 'number' ? pageId : null;
    };

    const rebuild = () => {
        const currentPages = engine.scene?.getPages?.() ?? [];
        const slidesWithMenuButton = getSlidesWithMenuButton();

        const pageIdsWithMenuButton = new Set<number>();
        slidesWithMenuButton.forEach((s: any) => {
            const pid = resolvePageIdForSlide(s);
            if (pid != null) {
                pageIdsWithMenuButton.add(pid);
            }
        });

        // Remove overlays for pages that no longer have showMenuButton enabled
        [...overlays.keys()].forEach((pageId) => {
            if (!pageIdsWithMenuButton.has(pageId) || !currentPages.includes(pageId)) {
                overlays.get(pageId)?.remove();
                overlays.delete(pageId);
            }
        });

        // Add overlays for pages that have showMenuButton enabled
        const globalMenuButtonColor = getGlobalMenuButtonColor();

        slidesWithMenuButton.forEach((slide: any) => {
            const pageId = resolvePageIdForSlide(slide);
            const buttonColor = globalMenuButtonColor;

            if (typeof pageId !== 'number') {
                return;
            }

            if (currentPages.includes(pageId) && !overlays.has(pageId)) {
                const el = createMenuButtonOverlay(engine, pageId, host, buttonColor, slide.isMenuSlide === true, opts.onNavigateToMenu ? () => {
                    const menuSlideId = getMenuSlideId();
                    if (menuSlideId && opts.onNavigateToMenu) {
                        opts.onNavigateToMenu(menuSlideId);
                    }
                } : undefined);
                overlays.set(pageId, el);
            } else if (overlays.has(pageId)) {
                // Update color if slide already has overlay
                const el = overlays.get(pageId)!;
                el.style.backgroundColor = buttonColor;
                el.style.color = '#fff';
                const icon = el.querySelector('.menu-button-icon') as HTMLElement | null;
                if (icon) {
                    setMenuButtonIcon(icon, slide.isMenuSlide === true);
                }
            }
        });
    };

    // Update overlay visibility and position for a specific page
    let currentPageId = resolveCurrentPageId() ?? engine.scene?.getCurrentPage?.() ?? 0;

    const refreshForPage = () => {
        rebuild();
        overlays.forEach((el, pageId) => {
            positionMenuButtonOverlay(engine, pageId, el, currentPageId);
        });
    };

    // Listen to host resizes - reposition for current page
    const handleResize = () => {
        requestAnimationFrame(() => {
            refreshForPage();
        });
    };

    const ro = new ResizeObserver(handleResize);
    ro.observe(host);

    // Listen to canvas for zoom events
    const canvas = engine.element as HTMLCanvasElement;
    let wheelTimeout: number | null = null;
    const handleWheel = () => {
        // Debounce wheel events to avoid excessive updates
        if (wheelTimeout) clearTimeout(wheelTimeout);
        wheelTimeout = window.setTimeout(() => {
            refreshForPage();
        }, 100);
    };

    if (canvas) {
        canvas.addEventListener('wheel', handleWheel);
    }

    // Use engine zoom change callback for immediate updates
    const unsubscribeZoomLevelChanged = engine.scene?.onZoomLevelChanged?.(() => {
        refreshForPage();
    }) ?? null;

    // Initial build
    refreshForPage();

    // Cleanup function
    const cleanup = () => {
        ro.disconnect();
        if (canvas) {
            canvas.removeEventListener('wheel', handleWheel);
        }
        if (wheelTimeout) clearTimeout(wheelTimeout);
        if (unsubscribeZoomLevelChanged) {
            try { unsubscribeZoomLevelChanged(); } catch {}
        }
        overlays.forEach((el) => el.remove());
        overlays.clear();
    };

    // Return update function and cleanup
    return {
        updateForActivePage: (pageId: number) => {
            currentPageId = pageId;
            refreshForPage();
        },
        cleanup
    };
}

function createMenuButtonOverlay(
    engine: any,
    pageId: number,
    host: HTMLElement,
    buttonColor: string = '#000000',
    isMenuSlide: boolean,
    onClick?: () => void
): HTMLDivElement {
    const overlay = document.createElement('div');
    overlay.className = 'menu-button-overlay-indicator';
    overlay.style.cssText = `
        position: absolute;
        z-index: 9998;
        background-color: ${buttonColor};
        border: 1px solid rgba(0, 0, 0, 0.1);
        border-radius: 12px;
        width: 52px;
        height: 52px;
        display: flex;
        align-items: center;
        justify-content: center;
        color: #fff;
        cursor: ${onClick ? 'pointer' : 'default'};
        pointer-events: ${onClick ? 'auto' : 'none'};
        transition: transform 0.2s ease, box-shadow 0.2s ease;
    `;

    // Add hover effect if clickable
    if (onClick) {
        overlay.addEventListener('mouseenter', () => {
            overlay.style.transform = 'scale(1.1)';
            overlay.style.boxShadow = '0 4px 8px rgba(0, 0, 0, 0.2)';
        });
        overlay.addEventListener('mouseleave', () => {
            overlay.style.transform = 'scale(1)';
            overlay.style.boxShadow = 'none';
        });
        overlay.addEventListener('click', onClick);
    }

    // Add menu icon
    const icon = document.createElement('div');
    icon.className = 'menu-button-icon';
    icon.style.pointerEvents = 'none'; // Let clicks pass through to overlay
    setMenuButtonIcon(icon, isMenuSlide);
    overlay.appendChild(icon);

    host.appendChild(overlay);
    return overlay;
}

function positionMenuButtonOverlay(
    engine: any,
    pageId: number,
    overlay: HTMLDivElement,
    activePageId?: number
): void {
    try {
        const currentPage = activePageId ?? engine.scene?.getCurrentPage?.();

        // Only show on current page
        if (currentPage !== pageId) {
            overlay.style.display = 'none';
            return;
        }

        // Get canvas element to calculate page position
        const canvas = engine.element as HTMLCanvasElement;
        if (!canvas) {
            overlay.style.display = 'none';
            return;
        }

        // Get canvas rect
        const canvasRect = canvas.getBoundingClientRect();

        // Get zoom level
        let zoom = 1;
        try {
            zoom = engine.scene.getZoomLevel?.() ?? 1;
        } catch (e) {
            zoom = 1;
        }

        // Get screen-space bounding box (same approach as rub your screen overlay)
        const raw = (engine.block as any).getScreenSpaceBoundingBoxXYWH?.([pageId]);
        if (!raw) {
            overlay.style.display = 'none';
            return;
        }

        // Accept both array and object return shapes
        const pageX = Array.isArray(raw) ? raw[0] : raw.x;
        const pageY = Array.isArray(raw) ? raw[1] : raw.y;
        const pageWidth = Array.isArray(raw) ? raw[2] : raw.width;
        const pageHeight = Array.isArray(raw) ? raw[3] : raw.height;

        // Get host (overlay-container) position
        const getOverlayContainer = () => {
            let el = document.getElementById('overlay-container');
            if (el) return el;
            const host = document.getElementById("root-shadow");
            const root = host?.shadowRoot || (host as any)?.__shadowRoot;
            return root?.getElementById('overlay-container');
        };
        const host = getOverlayContainer();
        const hostRect = host?.getBoundingClientRect();
        if (!hostRect) {
            overlay.style.display = 'none';
            return;
        }

        // Convert canvas-space bbox -> host-space coords
        const pageLeft = (canvasRect.left + pageX) - hostRect.left;
        const pageTop = (canvasRect.top + pageY) - hostRect.top;

        // Calculate scale factor from screen-space dimensions
        // pageWidth is in screen pixels, PRESENTATION_WIDTH is the base size
        const scaleFactor = pageWidth / PRESENTATION_WIDTH;

        // Scale button size based on zoom
        const baseButtonSize = 52;
        const baseIconSize = 24;
        const baseBorderRadius = 12;

        const buttonSize = baseButtonSize * scaleFactor;
        const iconSize = baseIconSize * scaleFactor;
        const borderRadius = baseBorderRadius * scaleFactor;

        // Update overlay size
        overlay.style.width = `${buttonSize}px`;
        overlay.style.height = `${buttonSize}px`;
        overlay.style.borderRadius = `${borderRadius}px`;

        // Update icon size
        const iconElement = overlay.querySelector('svg');
        if (iconElement) {
            iconElement.setAttribute('width', `${iconSize}`);
            iconElement.setAttribute('height', `${iconSize}`);
        }

        // Position in top-right corner with equal spacing from top and right edges
        // Use a fixed offset (scaled with page) from the edges
        const baseOffset = 26; // Half of button size for centered positioning
        const offset = baseOffset * scaleFactor;

        const left = pageLeft + pageWidth - buttonSize - offset;
        const top = pageTop + offset;

        overlay.style.left = `${left}px`;
        overlay.style.top = `${top}px`;
        overlay.style.display = 'flex';
    } catch (e) {
        console.error('[Menu Button Overlay] Error positioning:', e);
        overlay.style.display = 'none';
    }
}

/**
 * Mount functional menu buttons for the player/review page
 * These are actual clickable buttons that navigate to the menu slide
 *
 * Returns an object with:
 * - updateForActivePage: function to call when active page changes
 * - cleanup: function to call on unmount
 */
export function mountMenuButtonsForPlayer(
    engine: any,
    allSlideInfo: RefObject<VirtualizedSlideInfo[]>,
    opts: {
        container?: HTMLElement | null;
        onNavigateToMenu?: (targetSlideId: number, meta?: { fromSlideId?: number | null }) => void;
        clearHTMLOverlays?: boolean;
        getCurrentSlideId?: () => number | null | undefined;
        getCurrentPageId?: () => number | null | undefined;
        getPageIdForSlide?: (slideId: number | null | undefined) => number | null | undefined;
        getSlideIdForPage?: (pageId: number | null | undefined) => number | null | undefined;
    } = {}
): { updateForActivePage: (pageId: number) => void; cleanup: () => void } {
    const getOverlayContainer = () => {
        let el = document.getElementById('overlay-container');
        if (el) return el;
        const host = document.getElementById("root-shadow");
        const root = host?.shadowRoot || (host as any)?.__shadowRoot;
        return root?.getElementById('overlay-container');
    };
    const host = opts.container ?? getOverlayContainer() ?? document.body;
    const buttons = new Map<number, HTMLButtonElement>();

    if (opts.clearHTMLOverlays) {
        const existingButtons: HTMLElement[] = host.querySelectorAll('.menu-button-player');
        existingButtons.forEach(btn => btn.remove());
    }

    const resolveSlideInfo = () => allSlideInfo?.current ?? allSlideInfo;
    const resolveCurrentSlideId = () => opts.getCurrentSlideId?.() ?? null;
    const resolveCurrentPageId = () => opts.getCurrentPageId?.() ?? (engine.scene?.getCurrentPage?.() ?? null);

    // Find the menu slide (slide with isMenuSlide: true)
    const resolveSlideId = (slide: any): number | null => {
        const raw = slide?.id ?? slide?.slideId;
        return typeof raw === 'number' ? raw : null;
    };

    const getMenuSlideId = (): number | null => {
        const menuSlide = resolveSlideInfo()?.find((s: any) => s.isMenuSlide === true);
        return resolveSlideId(menuSlide);
    };

    const getFirstNonMenuSlideId = (): number | null => {
        const firstNonMenu = resolveSlideInfo()?.find((s: any) => s.isMenuSlide !== true);
        return resolveSlideId(firstNonMenu);
    };

    const isMenuSlide = (slideId: number | null | undefined) => {
        if (slideId == null) return false;
        return resolveSlideInfo()?.some((s: any) => resolveSlideId(s) === slideId && s.isMenuSlide === true) ?? false;
    };

    const getSlidesWithMenuButton = () => {
        const slides = resolveSlideInfo() ?? [];
        return slides.filter((s: any) => s && (s.showMenuButton === true || s.isMenuSlide === true));
    };
    const getGlobalMenuButtonColor = (): string => {
        const slides = resolveSlideInfo() ?? [];
        const slideWithMenuButton = slides.find((s: any) => s && (s.showMenuButton === true || s.isMenuSlide === true));
        return slideWithMenuButton?.menuButtonColor || 'rgb(0 0 0 / 75%)';
    };

    const resolvePageIdForSlide = (slide: any): number | null => {
        const slideId = resolveSlideId(slide);
        let pageId = opts.getPageIdForSlide?.(slideId);
        if (pageId == null && typeof slide?.scenePageId === 'number') {
            pageId = slide.scenePageId;
        }
        if (pageId == null && slideId != null && opts.getCurrentSlideId && opts.getCurrentPageId) {
            if (slideId === opts.getCurrentSlideId()) {
                pageId = opts.getCurrentPageId() ?? null;
            }
        }
        return typeof pageId === 'number' ? pageId : null;
    };

    let currentPageId = resolveCurrentPageId() ?? 0;
    let lastNonMenuSlideId: number | null = !isMenuSlide(resolveCurrentSlideId())
        ? resolveCurrentSlideId()
        : getFirstNonMenuSlideId();

    const handleMenuClick = () => {
        const menuSlideId = getMenuSlideId();
        const currentSlideId = resolveCurrentSlideId();
        if (!opts.onNavigateToMenu || !menuSlideId) {
            return;
        }

        if (currentSlideId === menuSlideId) {
            const targetSlideId = lastNonMenuSlideId ?? getFirstNonMenuSlideId();
            if (targetSlideId != null) {
                opts.onNavigateToMenu(targetSlideId, { fromSlideId: currentSlideId ?? undefined });
            }
            return;
        }

        if (currentSlideId != null && !isMenuSlide(currentSlideId)) {
            lastNonMenuSlideId = currentSlideId;
        }
        opts.onNavigateToMenu(menuSlideId, { fromSlideId: currentSlideId ?? undefined });
    };

    const rebuild = () => {
        const currentPages = engine.scene?.getPages?.() ?? [];
        const slidesWithMenuButton = getSlidesWithMenuButton();
        const pageIdsWithMenuButton = new Set<number>();

        slidesWithMenuButton.forEach((s: any) => {
            const pid = resolvePageIdForSlide(s);
            if (pid != null) {
                pageIdsWithMenuButton.add(pid);
            }
        });

        // Remove buttons for pages that no longer have showMenuButton enabled
        [...buttons.keys()].forEach((pageId) => {
            if (!pageIdsWithMenuButton.has(pageId) || !currentPages.includes(pageId)) {
                buttons.get(pageId)?.remove();
                buttons.delete(pageId);
            }
        });

        // Add buttons for pages that have showMenuButton enabled
        const globalMenuButtonColor = getGlobalMenuButtonColor();

        slidesWithMenuButton.forEach((slide: any) => {
            const pageId = resolvePageIdForSlide(slide);
            const buttonColor = globalMenuButtonColor;

            if (typeof pageId !== 'number') {
                return;
            }

            if (currentPages.includes(pageId) && !buttons.has(pageId)) {
                const btn = createMenuButton(engine, pageId, host, buttonColor, slide.isMenuSlide === true, handleMenuClick);
                buttons.set(pageId, btn);
            } else if (buttons.has(pageId)) {
                // Update color if slide already has button
                const btn = buttons.get(pageId)!;
                btn.style.backgroundColor = buttonColor;
                btn.style.color = '#fff';
                const icon = btn.querySelector('.menu-button-icon') as HTMLElement | null;
                if (icon) {
                    setMenuButtonIcon(icon, slide.isMenuSlide === true);
                }
            }
        });
    };

    // Update button visibility and position for a specific page
    const refreshForPage = () => {
        rebuild();
        buttons.forEach((btn, pageId) => {
            positionMenuButton(engine, pageId, btn, currentPageId);
        });
    };

    // Listen to host resizes - reposition for current page
    const handleResize = () => {
        requestAnimationFrame(() => {
            refreshForPage();
        });
    };

    const ro = new ResizeObserver(handleResize);
    ro.observe(host);

    // Listen to canvas for zoom events
    const canvas = engine.element as HTMLCanvasElement;
    let wheelTimeout: number | null = null;
    const handleWheel = () => {
        if (wheelTimeout) clearTimeout(wheelTimeout);
        wheelTimeout = window.setTimeout(() => {
            refreshForPage();
        }, 100);
    };

    if (canvas) {
        canvas.addEventListener('wheel', handleWheel);
    }

    // Use engine zoom change callback for immediate updates
    const unsubscribeZoomLevelChanged = engine.scene?.onZoomLevelChanged?.(() => {
        refreshForPage();
    }) ?? null;

    // Initial mount
    refreshForPage();

    // Cleanup function
    const cleanup = () => {
        ro.disconnect();
        if (canvas) {
            canvas.removeEventListener('wheel', handleWheel);
        }
        if (wheelTimeout) clearTimeout(wheelTimeout);
        if (unsubscribeZoomLevelChanged) {
            try { unsubscribeZoomLevelChanged(); } catch {}
        }
        buttons.forEach((btn) => btn.remove());
        buttons.clear();
    };

    // Return update function and cleanup
    return {
        updateForActivePage: (pageId: number) => {
            currentPageId = pageId;
            const nextSlideId =
                opts.getSlideIdForPage?.(pageId) ??
                resolveCurrentSlideId();

            if (nextSlideId != null && !isMenuSlide(nextSlideId)) {
                lastNonMenuSlideId = nextSlideId;
            }
            refreshForPage();
        },
        cleanup
    };
}

function createMenuButton(
    engine: any,
    pageId: number,
    host: HTMLElement,
    buttonColor: string = 'rgb(0 0 0 / 75%)',
    isMenuSlide: boolean,
    onClick: () => void
): HTMLButtonElement {
    const button = document.createElement('button');
    button.className = 'menu-button-player';
    button.style.cssText = `
        position: absolute;
        z-index: 9999;
        background-color: ${buttonColor};
        border: 1px solid rgba(0, 0, 0, 0.1);
        border-radius: 12px;
        width: 52px;
        height: 52px;
        display: flex;
        align-items: center;
        justify-content: center;
        cursor: pointer;
        pointer-events: auto;
        padding: 0;
        color: #fff;
        transition: transform 0.2s ease, box-shadow 0.2s ease;
    `;

    // Add hover effect
    button.addEventListener('mouseenter', () => {
        button.style.transform = 'scale(1.1)';
        button.style.boxShadow = '0 4px 8px rgba(0, 0, 0, 0.2)';
    });
    button.addEventListener('mouseleave', () => {
        button.style.transform = 'scale(1)';
        button.style.boxShadow = 'none';
    });

    // Add menu icon
    const icon = document.createElement('div');
    icon.className = 'menu-button-icon';
    icon.style.pointerEvents = 'none'; // Let clicks pass through to button
    setMenuButtonIcon(icon, isMenuSlide);
    button.appendChild(icon);

    // Add click handler
    button.addEventListener('click', onClick);

    host.appendChild(button);
    return button;
}

function positionMenuButton(
    engine: any,
    pageId: number,
    button: HTMLButtonElement,
    activePageId?: number
): void {
    try {
        const currentPage = activePageId ?? engine.scene?.getCurrentPage?.();

        // Only show on current page
        if (currentPage !== pageId) {
            button.style.display = 'none';
            return;
        }

        // Get canvas element to calculate page position
        const canvas = engine.element as HTMLCanvasElement;
        if (!canvas) {
            button.style.display = 'none';
            return;
        }

        // Get canvas rect
        const canvasRect = canvas.getBoundingClientRect();

        // Get screen-space bounding box
        const raw = (engine.block as any).getScreenSpaceBoundingBoxXYWH?.([pageId]);
        if (!raw) {
            button.style.display = 'none';
            return;
        }

        // Accept both array and object return shapes
        const pageX = Array.isArray(raw) ? raw[0] : raw.x;
        const pageY = Array.isArray(raw) ? raw[1] : raw.y;
        const pageWidth = Array.isArray(raw) ? raw[2] : raw.width;
        const pageHeight = Array.isArray(raw) ? raw[3] : raw.height;

        // Get host (overlay-container) position
        const getOverlayContainer = () => {
            let el = document.getElementById('overlay-container');
            if (el) return el;
            const host = document.getElementById("root-shadow");
            const root = host?.shadowRoot || (host as any)?.__shadowRoot;
            return root?.getElementById('overlay-container');
        };
        const host = getOverlayContainer();
        const hostRect = host?.getBoundingClientRect();
        if (!hostRect) {
            button.style.display = 'none';
            return;
        }

        // Convert canvas-space bbox -> host-space coords
        const pageLeft = (canvasRect.left + pageX) - hostRect.left;
        const pageTop = (canvasRect.top + pageY) - hostRect.top;

        // Calculate scale factor from screen-space dimensions
        const scaleFactor = pageWidth / PRESENTATION_WIDTH;

        // Scale button size based on zoom
        const baseButtonSize = 52;
        const baseIconSize = 24;
        const baseBorderRadius = 12;

        const buttonSize = baseButtonSize * scaleFactor;
        const iconSize = baseIconSize * scaleFactor;
        const borderRadius = baseBorderRadius * scaleFactor;

        // Update button size
        button.style.width = `${buttonSize}px`;
        button.style.height = `${buttonSize}px`;
        button.style.borderRadius = `${borderRadius}px`;

        // Update icon size
        const iconElement = button.querySelector('svg');
        if (iconElement) {
            iconElement.setAttribute('width', `${iconSize}`);
            iconElement.setAttribute('height', `${iconSize}`);
        }

        // Position in top-right corner with equal spacing from top and right edges
        const baseOffset = 26;
        const offset = baseOffset * scaleFactor;

        const left = pageLeft + pageWidth - buttonSize - offset;
        const top = pageTop + offset;

        button.style.left = `${left}px`;
        button.style.top = `${top}px`;
        button.style.display = 'flex';
    } catch (e) {
        console.error('[Menu Button Player] Error positioning:', e);
        button.style.display = 'none';
    }
}